import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle, UserPlus, MessageSquare, Radio, CalendarClock } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

const ICONS = {
  follow: { icon: UserPlus, color: "text-emerald-400" },
  like: { icon: Heart, color: "text-red-400" },
  comment: { icon: MessageSquare, color: "text-violet-400" },
  message: { icon: MessageCircle, color: "text-emerald-400" },
  party: { icon: Radio, color: "text-red-400" },
  party_reminder: { icon: CalendarClock, color: "text-amber-400" },
};

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);
  const prevUnread = useRef(null);
  const audioCtxRef = useRef(null);

  const playChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = now + i * 0.11;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.25);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(start); osc.stop(start + 0.3);
      });
    } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
      if (prevUnread.current !== null && data.unread > prevUnread.current) playChime();
      prevUnread.current = data.unread;
    } catch {}
  }, [playChime]);

  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read"); setUnread(0); prevUnread.current = 0; setItems((p) => p.map((n) => ({ ...n, read: true }))); } catch {}
    }
  };

  const clickItem = (n) => {
    setOpen(false);
    if (n.type === "message") navigate(`/messages/${n.actor_id}`);
    else if (n.type === "party" && n.target_id) navigate(`/party/${n.target_id}`);
    else if (n.type === "party_reminder") navigate("/feed");
    else if (n.type === "follow") navigate(`/artist/${n.actor_id}`);
    else navigate("/feed");
  };

  return (
    <div ref={boxRef} className="relative">
      <button onClick={toggle} data-testid="notification-bell"
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <motion.span key={unread} data-testid="notification-badge"
            initial={{ scale: 0.5 }} animate={{ scale: [1.5, 1] }} transition={{ duration: 0.35 }}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 top-11 w-80 max-w-[calc(100vw-2rem)] bg-[#181824] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
            <div className="px-4 py-3 border-b border-white/10 font-bold text-sm">Notifications</div>
            <div className="max-h-96 overflow-y-auto hide-scrollbar">
              {items.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 text-sm">
                  <Bell className="w-7 h-7 mx-auto mb-2 opacity-40" /> No notifications yet
                </div>
              ) : items.map((n) => {
                const cfg = ICONS[n.type] || ICONS.like;
                const Icon = cfg.icon;
                return (
                  <button key={n.id} onClick={() => clickItem(n)} data-testid="notification-item"
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors ${!n.read ? "bg-emerald-500/[0.06]" : ""}`}>
                    <span className="relative shrink-0">
                      {n.actor_avatar ? <img src={mediaUrl(n.actor_avatar)} alt="" className="w-9 h-9 rounded-full object-cover" />
                        : <span className="w-9 h-9 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-300 font-bold text-sm">{n.actor_name?.[0]?.toUpperCase() || "A"}</span>}
                      <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#181824] flex items-center justify-center">
                        <Icon className={`w-3 h-3 ${cfg.color}`} />
                      </span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-zinc-200"><b>{n.actor_name}</b> {n.text}</p>
                      <p className="text-xs text-zinc-500 font-mono-nu">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
